import { SelectorChain } from '@fullstory/proto/fs/selectors';
import type { FsId } from '@fullstory/recording-api';
import { NodePath } from '@fullstory/renderer-shared';
import {
  AttributeKind,
  NormalizedTagName,
  toNormalizedAttributeName,
  toNormalizedTagName,
} from '@fullstory/rewriters';
import { AttrDeferred, AttrExcluded, AttrMasked } from '@fullstory/shared/blockattrs';
import { renderMasked } from '@fullstory/shared/masktext';
import {
  COMMENT_NODE_TAG,
  DOCUMENT_NODE_TAG,
  POLYFILL_SHADOW_NODE_TAG,
  SHADOW_NODE_TAG,
  TEXT_NODE_TAG,
} from '@fullstory/shared/node_decoder';
import { uaIsChrome } from '@fullstory/useragent';
import { DirtyBit, dirtyBitsToString } from './dirtybits';
import type { FsDocument } from './fsdocument';
import type { FsShadowRootLike } from './fsdocumentorshadowroot';
import type { FsFramelikeElement } from './fsframelikeelement';
import type { FsMediaElement } from './fsmediaelement';
import type { FsText } from './fstext';
import type { RenderedNode } from './renderednode';
import { ScrollState } from './scrollstate';
import { setValue } from './setvalue';
import { SvgNamespace } from './svg';

// Specific types of FsNodes. Used to narrow the type of an FsNode's `dom`
// property via methods like `FsNode.prototype.isElement()`.
export type FsElement = FsNode & { dom: Element };

// An FsNode which is known to be in the document.
export type AttachedFsNode = FsNode & { fsid: number };

// Super simple DOMNode representation.
export class FsNode {
  private _attached: boolean = false;
  private _properties: Record<string, unknown> = {};
  isAttached(): this is AttachedFsNode {
    return this._attached;
  }
  setAttached(): void {
    if (this._attached === true) {
      return;
    }
    this._attached = true;
    this.ownerDoc.updateNodeCategories(this);
  }
  setDetached(): void {
    if (this._attached === false) {
      return;
    }
    this._attached = false;
    this.ownerDoc.updateNodeCategories(this);
  }

  /**
   * @returns true if this node is a "helper node", meaning that it was created
   * as an implementation detail of playback, rather than being derived from the
   * recording. Helper nodes don't have an FsId since they didn't exist at
   * recording time; in a sense, they're not part of the document.
   */
  isHelper(): this is FsNode & { fsid: undefined } {
    return this.fsid === undefined;
  }

  /** @returns the tag name of this node, including any namespace prefix. */
  get namespacedTagName(): string {
    return this.namespace === SvgNamespace ? `svg:${this.tag}` : this.tag;
  }

  parent: FsNode | null = null;
  firstChild: FsNode | null = null;
  lastChild: FsNode | null = null;
  prev: FsNode | null = null;
  next: FsNode | null = null;

  private _shadow: FsShadowRootLike | null = null;
  get shadow() {
    return this._shadow;
  }

  setShadow(shadow: FsShadowRootLike): void {
    if (this._shadow) {
      throw new Error('double-set shadow root');
    }

    this._shadow = shadow;
    this._shadow.attachShadowRootTo(this);
    this.setDirtyBit(DirtyBit.ShadowRoot);
  }

  private _dom: RenderedNode | null = null;
  get dom() {
    return this._dom;
  }
  set dom(value: RenderedNode | null) {
    if (value) {
      value._fs = this;
    }
    this._dom = value;
  }

  attrs: { [name: string]: string } = {}; // Mirrors what is in the DOM, or should be reified to the DOM.

  // If this node is scrollable, stores the scroll position and related metadata.
  private _scrollState: ScrollState | undefined;

  // True if we know for sure that this node is scrollable.
  get knownScrollable() {
    return this._scrollState !== undefined;
  }

  inSvg = false;

  // A node at the end of this one's child list, not
  // corresponding to any recorded node.
  private _footerChild: FsNode | null = null;

  private _helperStyles = new Map<string, { value: string; priority: string }>();
  private _value: string | undefined;
  protected _excluded: boolean = false;
  protected _masked: boolean = false;
  protected _deferred: boolean = false;

  private _dirtyAttrs = new Set<string>();
  private _dirtyBits: number = DirtyBit.None;

  readonly path?: NodePath;

  constructor(
    public readonly ownerDoc: FsDocument,
    public readonly tag: NormalizedTagName,
    public readonly namespace?: string,
    public readonly fsid?: FsId,
  ) {
    if (fsid !== undefined) {
      this.path = new NodePath(ownerDoc.path.toFramePath(), fsid);
    }
    if (tag === 'svg') {
      this.inSvg = true;
    }

    // HACK: Prevent the customer's LastPass from autofilling input elements (SC171984).
    // Also prevent autofill on select elements, since sometimes this can be stored in viewer's autofill cache (DXA-2982).
    if (this.tag === 'input' || this.tag === 'select') {
      this.setAttr('data-lpignore', 'true');
      this.setAttr('autocomplete', 'off');
    }
  }

  get [Symbol.toStringTag]() {
    // Make toString()'s output more useful for FsNodes.
    return `${this.constructor.name} ${this.fsid} ${this.tag}`;
  }

  static isElementTag(tagName: string): boolean {
    return (
      tagName != '!doctype' &&
      tagName != DOCUMENT_NODE_TAG &&
      tagName != TEXT_NODE_TAG &&
      tagName != COMMENT_NODE_TAG &&
      tagName != SHADOW_NODE_TAG &&
      tagName !== POLYFILL_SHADOW_NODE_TAG
    );
  }

  isElement(): this is FsElement {
    return FsNode.isElementTag(this.tag);
  }

  isFrame(): this is FsFramelikeElement {
    return false;
  }

  isMediaElement(): this is FsMediaElement {
    return false;
  }

  isText(): this is FsText {
    return false;
  }

  /**
   * Invalidate the DOM subtree for this node. The subtree will be rebuilt from
   * scratch on the next flush.
   *
   * force will cause this to invalidate the properties of this subtree even if we have not yet flushed
   * (useful if a node is moved before it's flushed)
   */
  invalidateDomSubtree(force = false): void {
    if (!force && !this.dom) {
      return;
    }

    if (this.dom) {
      // NOTE: when we invalidate, we need to make sure that _flushChildrenDom can detect
      // that this node has been removed; setting _fs accomplishes this
      this.dom._fs = undefined;
      this.dom = null;
    }

    this.invalidateProperties();

    if (this._shadow) {
      this._shadow.invalidateDomSubtree();
    }

    this.forEachChild(c => c.invalidateDomSubtree());
  }

  /**
   * Notify that this node is excluded. This is a one-way operation, since if a
   * node stops being excluded it will be removed and re-inserted.
   */
  setExcluded(): void {
    this._excluded = true;

    if (!this.isElement()) {
      return;
    }

    // Make sure this element is marked as excluded using the attribute we use
    // in modern recordings. Older recordings used a class instead.
    this.setAttr('_fs_blocked', 'true', /* skipRewrite = */ true);
  }

  setMasked(): void {
    this._masked = true;
  }

  setDeferred(): void {
    this._deferred = true;
  }

  isDeferred(): boolean {
    return this._deferred;
  }

  resolveDeferred(): void {
    this._deferred = false;
    this.removePlaceHolderSize();
  }

  // Sets an attribute's value. If value is null, it will be removed.
  // We sometimes need to skip rewriting due to us poking in things used for playback (like system stylesheets).
  setAttr(rawName: string, recordedValue: string | null, skipRewrite?: boolean): void {
    // If the value was masked, render it to something human-readable. Attribute values with specific formats (e.g., URLs)
    // will never be masked.
    const requestedValue = renderMasked(recordedValue);

    // setAttr is the root-most attribute handler. Always lowercase attribute names.
    // All UAs treat attributes as case insensitive for native DOM and style matching.
    // Note: don't convert attribute names for SVG, these are case-sensitive. e.g. viewBox, viewTarget, etc
    const attrKind: AttributeKind = this.inSvg ? 'svg' : 'html';
    let name = toNormalizedAttributeName(rawName, attrKind);

    // SVG2 uses 'href' instead of 'xlink:href', and 'xlink:href' is now
    // deprecated. (Safari 12 hasn't implemented support for 'href' yet,
    // though.) They're supposed to be equivalent, but in Chrome, 'href' seems
    // to pick up changes to references to other elements within the document
    // ('#foo') much better than 'xlink:href'. Although rewriting to 'href'
    // might stomp on an 'href' attribute in the recording, the improvement in
    // behavior is major enough to be worth the risk. See CH50391.
    if (uaIsChrome && this.inSvg && name == 'xlink:href') {
      name = toNormalizedAttributeName('href', attrKind);
    }

    // Rewrite the attribute, unless the caller specifically requested not to.
    let rewrittenValue: string | null;
    if (skipRewrite) {
      rewrittenValue = requestedValue;
    } else {
      rewrittenValue = this.ownerDoc.nodeRewriter.rewriteAttrForNode(this, name, requestedValue);
    }

    // if we change the `name` attribute on a radio button, its group will now be changed
    // so unassign it from its current group
    // NOTE (eugenechang) this code needs to run before the actual attr is set
    if (name === 'name' && this.tag === 'input' && this.attr('type') === 'radio') {
      this.ownerDoc.setRadioValue(this, undefined, this.attr('name'));

      // associate it to the new group
      this.ownerDoc.setRadioValue(this, this.attr('value') || 'false', rewrittenValue ?? '');
    }

    if (name == 'id') {
      const staleId = this.attrs['id'];
      if (staleId) {
        delete this.ownerDoc.byDomId[staleId];
      }
      if (rewrittenValue !== null) {
        this.ownerDoc.byDomId[rewrittenValue] = this;
      }
    }

    if (rewrittenValue === null) {
      // Bad attribute, or attribute removal. Treated the same.
      // TODO(bendean): If this attribute is associated with a loading
      // resource, resolve or delete that loading resource promise.
      if (!(name in this.attrs)) {
        return; // Nothing to do.
      }
      delete this.attrs[name];
    } else {
      if (this.attrs[name] === rewrittenValue) {
        return; // No change; skip the remaining work.
      }
      this.attrs[name] = rewrittenValue;
    }

    this.onAttrChanged(name, rewrittenValue);

    // If we're setting the 'style' attribute, we'll need to reapply helper
    // inline styles afterwards.
    if (name === 'style') {
      this.setDirtyBit(DirtyBit.HelperInlineStyle);
    }

    this._setAttrDirty(name);

    // Mark this element as excluded if it has the special attribute added by
    // the recording script.
    // TODO(sethf): The '_fs_block' class is supported for backwards
    // compatibility with sessions that were recorded before August 2019. We
    // should remove support for it once enough time has passed.
    if (name === AttrExcluded || (name === 'class' && rewrittenValue?.includes('_fs_block'))) {
      this.setExcluded();
    }

    if (name === AttrMasked) {
      this.setMasked();
    }

    if (name === AttrDeferred) {
      this.setDeferred();
    }

    if (
      name === 'checked' &&
      this.tag === 'input' &&
      this.attr('type') === 'radio' &&
      // if `checked` was set for this element, checked attribute will now no op
      typeof this._properties['checked'] === 'undefined'
    ) {
      // when we encounter `checked` attributes in the dom, update our doc accordingly
      // any value of `checked` (except absent, indicated by null) would indicate this radio should be checked
      this.ownerDoc.setRadioValue(this, rewrittenValue !== null ? 'true' : 'false');
    }
  }

  /**
   * Notify that the value of a virtual attribute has changed. The new attribute
   * value may not have been to flushed to the DOM yet.
   *
   * Subclasses may override this method to react to attribute changes at the
   * vDOM level. To react to attribute changes at the DOM level, subclasses
   * should override {@link flushAttr} instead.
   */
  protected onAttrChanged(_name: string, _value: string | null): void {}

  /**
   * Flush an attribute to the DOM.
   *
   * Subclasses may override this method to add node-type-specific behaviors for
   * particular attributes.
   */
  protected flushAttr(dom: Node, name: string, value: string | null): void {
    if (!this.isElement()) {
      console.warn(`Flushing attribute on non-Element ${this}: '${name}' = '${value}'`);
      return;
    }

    if (!dom) {
      const attached = this.isAttached() ? 'attached' : 'detached';
      console.warn(
        `Flushing attribute on ${attached} unflushed node ${this}: '${name}' = '${value}'`,
      );
      return;
    }

    const elem: Element = dom as Element;
    if (value === null) {
      elem.removeAttribute(name);
      return;
    }

    // Be resilient to bad attributes here. They can happen in 'legitimate' cases,
    // as when a page author typo's a raw ";" in an element. Apparently the HTML parser
    // will happily create an attribute out of some kinds of bad input, but setAttribute()
    // is stricter.
    try {
      elem.setAttribute(name, value);

      // Hack for broken SVG images, for xlink:href and references to other elements by id.
      if (
        this.inSvg &&
        this.ownerDoc.ctx.flags.features.AppPlaybackEnableSvgHrefWorkaround &&
        (name === 'xlink:href' || name === 'href' || value.indexOf('#') > -1)
      ) {
        this._tellParentSVGToFixItselfLater();
      }
    } catch (e) {
      console.log(
        `Unexpected exception setting attribute in FsNode <${this.tag}> - ${name}='${value}': ${e}`,
      );
    }
  }

  setValue(recordedValue: string): void {
    const value = renderMasked(recordedValue);

    // Avoid setting multiple values in the same radio button group to true
    if (this.tag == 'input' && this.attr('type') == 'radio') {
      this.ownerDoc.setRadioValue(this, value);
      return;
    }

    this._value = value;
    this.setDirtyBit(DirtyBit.Value);
  }

  // @VisibleForTesting
  getValue(): string | undefined {
    return this._value;
  }

  setProp(name: string, value: unknown): void {
    this._properties[name] = value;
    this.setDirtyBit(DirtyBit.Property);
  }

  /**
   * Set a "helper" inline style on this node. Helper styles are implementation
   * details of playback; they don't come from the recorded page. They're
   * automatically reapplied if this node's 'style' attribute changes.
   */
  setHelperInlineStyle(name: string, value: string, priority: string = '') {
    this._helperStyles.set(name, { value, priority });
    this.setDirtyBit(DirtyBit.HelperInlineStyle);
  }

  removeHelperInlineStyle(name: string) {
    this._helperStyles.delete(name);
    this.setDirtyBit(DirtyBit.HelperInlineStyle);
  }

  /**
   * Set a placeholder size for this node. This captures the space that the node
   * would have occupied if its contents weren't excluded or masked.
   */
  setPlaceholderSize(width: number, height: number): void {
    // Only set these attributes for excluded or masked elements
    // since we also fire this event to record the size of inner scroll elements
    if (!this._excluded && !this._masked && !this._deferred) {
      return;
    }

    // If we set the 'width' and 'height' of an element directly, the
    // layout behavior of descendants can change. When an element does not
    // have an explicit size, descendants with percentage width/height
    // will compute their preferred size as if they had a width or height
    // of 'auto' to avoid a circular dependency between their size and the
    // their ancestor. Setting 'width' and 'height' would eliminate that
    // circular dependency, causing the percentage heights on those
    // descendants to actually have an effect. To avoid triggering this
    // layout behavior change, we set the placeholder size indirectly
    // using 'min-' and 'max-' constraints.
    const [widthPx, heightPx] = [`${width}px`, `${height}px`];
    this.setHelperInlineStyle('min-width', widthPx);
    this.setHelperInlineStyle('max-width', widthPx);
    this.setHelperInlineStyle('min-height', heightPx);
    this.setHelperInlineStyle('max-height', heightPx);
    this.setAttr('_fs_placeholder_size', 'true');
  }

  removePlaceHolderSize(): void {
    this.removeHelperInlineStyle('min-width');
    this.removeHelperInlineStyle('max-width');
    this.removeHelperInlineStyle('min-height');
    this.removeHelperInlineStyle('max-height');
    this.setAttr('_fs_placeholder_size', null);
  }

  // Determines whether an attribute is present, including from pending attribute sets.
  hasAttr(name: string): boolean {
    return name.toLowerCase() in this.attrs;
  }

  // Gets the given attribute's value, either from pending or normal attributes.
  attr(name: string): string {
    const key = name.toLowerCase();
    if (key in this.attrs) {
      return this.attrs[key];
    }
    return '';
  }

  // Return `this._footerChild`, creating it if necessary.
  ensureFooterChild(): FsNode {
    if (this._footerChild) {
      return this._footerChild;
    }
    const footerChild = this.ownerDoc.createHelperNode('div');
    footerChild.setAttr('id', 'fs-footer-child');
    footerChild.setAttr('style', 'display: none;');
    this.appendChild(footerChild);

    // This assignment needs to come after the appendChild call.
    return (this._footerChild = footerChild);
  }

  // Inserts another Node into this one (optionally before a reference node, null to append).
  insert(child: FsNode, realBefore: FsNode | null): void {
    // We need to support the reparenting of child nodes because that's how we currently model polyfill shadow slots' assigned nodes.
    if (child.parent) {
      // reset the bits and remove the dom so it would be recreated
      child.invalidateDomSubtree(true);

      // since target child's parent is not set to us yet, child.invalidateDomSubtree will not propagate
      // through this node's subtree; mark descendent to this node to ensure child is reflushed
      this.setDirtyBit(DirtyBit.Descendants);

      // remove should be called last so we would have updated its parent as necessary
      child.remove();
    }

    const before = !realBefore && this._footerChild ? this._footerChild : realBefore;
    this._insert(child, before);
    if (this.inSvg) {
      child.inSvg = true;
    }
    child.setAttached();
  }

  appendChild(node: FsNode): void {
    this.insert(node, null);
  }

  removeChild(child: FsNode): void {
    child.remove();
    this.ownerDoc.updateNodeCategories(child);
  }

  // TODO(bendean): Currently this doesn't cause this node
  // to get GC'd, since it's still referenced by `ownerDoc.byId`.
  // If we change that, don't forget to clean up references from
  // `FsCSSOMSheet.ownerNodes`.
  remove(): void {
    this.inSvg = false;

    if (this.parent) {
      this.parent.setDirtyBit(DirtyBit.ChildList);
      if (this.parent.firstChild == this) {
        this.parent.firstChild = this.next;
      }
      if (this.parent.lastChild == this) {
        this.parent.lastChild = this.prev;
      }
    }

    if (this.prev) {
      this.prev.next = this.next;
    }
    if (this.next) {
      this.next.prev = this.prev;
    }

    this.parent = null;
    this.prev = null;
    this.next = null;
  }

  // If this is the first time the node has scrolled, initializes the scroll
  // state and performs scroll-related bookkeeping. Returns the now-initialized
  // scroll state.
  private initializeScrollStateIfNeeded(): ScrollState {
    if (this._scrollState) {
      return this._scrollState;
    }

    if (this.tag == 'html' || this.tag == 'body') {
      // There's a single document-level scroll state, regardless of which
      // element is used to access it.
      this._scrollState = this.ownerDoc.scrollState;
    } else {
      // Other scrollable elements have their own scroll state.
      this._scrollState = new ScrollState();
    }

    // This is the first time we've received a scroll event for this element;
    // we now know it's scrollable. Disable smooth scrolling; it makes scroll
    // position changes asynchronous, which can cause playback to diverge
    // visually from the event stream. It can also sometimes prevent
    // programmatic scrolling.
    // TODO(sethf): Despite all that, it honestly looks better. It would be
    // worth investigating whether we can reenable smooth scrolling, at least
    // sometimes.
    this.ownerDoc.updateNodeCategories(this);

    return this._scrollState;
  }

  /**
   * @returns the DOM nodes that should scroll when this vDOM node scrolls, or
   * the empty list if there isn't one available.
   */
  private getScrollingElements(): HTMLElement[] {
    const thisElem = this.dom as HTMLElement;
    if (!thisElem || !this.ownerDoc.document) {
      return [];
    }

    // If we're not dealing with document-level scrolling, we always want to
    // scroll with our own DOM node.
    if (this.tag !== 'html' && this.tag !== 'body') {
      return [thisElem];
    }

    // We're *probably* dealing with document-level scrolling, which means we
    // need to scroll 'scrollingElement', but not necessarily. It's possible to
    // inhibit document-level scrolling by setting certain styles on the
    // document element - e.g., 'html { position: fixed }'. In that situation,
    // we may receive scroll events on <body> that really do need to be applied
    // to <body> and not to 'scrollingElement'. Unfortunately it's hard to tell
    // whether we're in that situation or not, so to be safe we attempt to
    // scroll *both* elements in the hope that at least one of them will work.
    const scrollingElem = this.ownerDoc.document.scrollingElement as HTMLElement;
    if (scrollingElem && scrollingElem !== thisElem) {
      return [scrollingElem, thisElem];
    }

    return [thisElem];
  }

  // Try to set the scroll position of this node to the provided values.
  setScroll(scrollLeft: number, scrollTop: number, when: number): void {
    const scrollState = this.initializeScrollStateIfNeeded();

    if (when < scrollState.watermark) {
      return;
    }

    scrollState.left = scrollLeft;
    scrollState.top = scrollTop;
    scrollState.watermark = when;
    this.setDirtyBit(DirtyBit.ScrollPosition);
  }

  flushScrollPosition(): void {
    const scrollState = this.initializeScrollStateIfNeeded();

    const elems = this.getScrollingElements();
    for (const elem of elems) {
      elem.scrollLeft = scrollState.left;
      elem.scrollTop = scrollState.top;
    }
  }

  /**
   * Sets this node and all scrollable child nodes to the top left.
   */
  zeroScrollPositions(): void {
    // Sets the ScrollState in case other events fire flushScrollPosition() later
    const scrollState = this.initializeScrollStateIfNeeded();
    scrollState.left = 0;
    scrollState.top = 0;
  }

  get childNodes() {
    const children: FsNode[] = [];
    this.forEachChild(c => children.push(c));
    return children;
  }

  flush(): void {
    // If we were newly inserted, we're not anymore.
    this._clearDirtyBit(DirtyBit.NewlyAttached);

    // Get the child list in order, removing any absent nodes, and appending new ones.

    // Flush shadow root.
    if (this._hasDirtyBit(DirtyBit.ShadowRoot)) {
      this.shadow?.flushShadowRoot(this.dom as HTMLElement);
      this._clearDirtyBit(DirtyBit.ShadowRoot);
    }

    // Flush children.
    this._flushChildrenDom();

    if (this._hasDirtyBit(DirtyBit.Descendants)) {
      this._clearDirtyBit(DirtyBit.Descendants);
      this.forEachChild(c => this.renderChild(c) && c.flush());
      if (this.shadow) {
        this.shadow.flush();
      }
    }

    // Ensure this node's attributes and properties are set. Do this *after*
    // children, because some attributes (e.g., select.value) won't work before
    // children exist.
    if (this.dom) {
      this.flushProperties(this.dom);
    }
  }

  /**
   * overridable function to whether a parent should flush a particular child
   * default implementation: render everything except for direct child of a polyfill shadow host, except for shadowroot
   */
  protected renderChild(child: FsNode): boolean {
    return this.shadow?.tag !== POLYFILL_SHADOW_NODE_TAG || child.tag === POLYFILL_SHADOW_NODE_TAG;
  }

  private _flushChildrenDom() {
    if (!this._hasDirtyBit(DirtyBit.ChildList)) {
      return;
    }

    // iterate through real dom, remove any not in vdom
    let curRealChild = this.dom!.firstChild;
    while (curRealChild) {
      if (curRealChild._fs?.parent === this) {
        curRealChild = curRealChild.nextSibling;
        continue;
      }
      const nextChild = curRealChild.nextSibling;
      this.dom!.removeChild(curRealChild);
      curRealChild = nextChild;
    }

    // iterate through vdom, insert any nodes non-connected
    // NOTE this relies on the fact that no nodes can swap order
    // and instead they should end up beng new nodes
    for (let curChild = this.firstChild; curChild; curChild = curChild.next) {
      if (!this.renderChild(curChild)) {
        continue;
      }
      curChild.ensureNode();
      if (curChild.dom!.isConnected) {
        continue;
      }
      const prev = curChild._previousRenderedSibling?.dom! as ChildNode | undefined;
      if (prev) {
        prev.after(curChild.dom!);
      } else {
        this.dom!.insertBefore(curChild.dom!, this.dom!.firstChild);
      }
    }

    this._clearDirtyBit(DirtyBit.ChildList);
  }

  private get _previousRenderedSibling(): FsNode | null {
    let prev = this.prev;
    while (prev && !(this.parent?.renderChild(prev) ?? true)) {
      prev = prev.prev;
    }

    return prev;
  }

  ensureNode(): void {
    if (this.dom) {
      return;
    }

    if (!this.ownerDoc.document) {
      throw new Error(`Calling ensureNode() in a detached document`);
    }

    this.dom = this.createNode(this.ownerDoc.document);
  }

  /**
   * Create a real DOM node for this vDOM node.
   *
   * Subclasses should override this method.
   *
   * @param doc - The document which should be used to create the new DOM node.
   * This is equivalent to 'this.ownerDoc.document' except that it's guaranteed
   * to be non-null.
   * @returns the created DOM node.
   *
   * TODO(sethf): Make this abstract once it's feasible.
   */
  protected createNode(doc: Document): Node | null {
    try {
      if (this.namespace) {
        return doc.createElementNS(this.namespace, this.tag);
      }
      return doc.createElement(this.tag);
    } catch (e) {
      // Fallback for absurd node names (innerHTML can be very permissive, but not createElement()).
      return doc.createElement('div');
    }
  }

  /**
   * Set all dirty bits relevant to this node, so that it will be recreated on
   * the next flush.
   *
   * Subclasses should override this method.
   *
   * TODO(sethf): Make this abstract once it's feasible.
   */
  protected invalidateProperties(): void {
    // Behave as if this was a new node.
    this.setDirtyBit(DirtyBit.NewlyAttached);

    if (this.parent) {
      this.parent.setDirtyBit(DirtyBit.ChildList);
    }
    if (this._shadow) {
      this.setDirtyBit(DirtyBit.ShadowRoot);
    }

    for (const attrName of Object.keys(this.attrs)) {
      this._setAttrDirty(attrName);
    }

    if (this._helperStyles.size > 0) {
      this.setDirtyBit(DirtyBit.HelperInlineStyle);
    }
    if (typeof this._value === 'string') {
      this.setDirtyBit(DirtyBit.Value);
    }
  }

  /** @returns true if this node is dirty. */
  isDirty(): boolean {
    return this._dirtyBits !== 0 || this._dirtyAttrs.size > 0;
  }

  /** @returns a string representation of the dirty bits of this node. */
  dirtyBitsToString(): string {
    return dirtyBitsToString(this._dirtyBits);
  }

  protected _hasDirtyBit(bit: DirtyBit): boolean {
    return (this._dirtyBits & bit) !== 0;
  }

  /**
   * Set the provided dirty bit on this node and mark ancestors as having a
   * dirty descendant.
   */
  setDirtyBit(bit: DirtyBit): void {
    if (this.ownerDoc.inFlush) {
      console.error(`Setting dirty bit ${bit} while flushing!`);
    }

    const oldDirtyBits = this._dirtyBits;

    if (this.shadow?.tag === POLYFILL_SHADOW_NODE_TAG && bit === DirtyBit.ChildList) {
      // when setting ChildList bit of a polyfill shadow host (because it has a shadow), also set shadowRoot bit
      // we need to do this because the shadowRoot flushes to the same dom
      // TODO: find a way to test this behavior
      this.setDirtyBit(DirtyBit.ShadowRoot);
      this.shadow.setDirtyBit(DirtyBit.ChildList);
    }

    this._dirtyBits = this._dirtyBits | bit;
    if (this._dirtyBits === oldDirtyBits) {
      return;
    }

    this._propagateDescendantsDirtyBitToAncestors();
  }

  /** TODO(sethf): Temporarily public to support FsText. */
  _clearDirtyBit(bit: DirtyBit): void {
    this._dirtyBits = this._dirtyBits & ~bit;
  }

  private _hasDirtyAttrs(): boolean {
    return this._dirtyAttrs.size > 0;
  }

  /**
   * Set the provided attribute dirty and mark ancestors as having a dirty
   * descendant.
   */
  private _setAttrDirty(attrName: string): void {
    if (this.ownerDoc.inFlush) {
      console.error(`Setting attribute '${attrName}' dirty while flushing!`);
    }

    if (this._dirtyAttrs.has(attrName)) {
      return;
    }

    this._dirtyAttrs.add(attrName);
    this._propagateDescendantsDirtyBitToAncestors();
  }

  private _clearDirtyAttrs(): void {
    this._dirtyAttrs.clear();
  }

  private _propagateDescendantsDirtyBitToAncestors(): void {
    let current = this.parent;
    const seen = new WeakMap<FsNode, boolean>();
    while (current && !(current._dirtyBits & DirtyBit.Descendants)) {
      // protect against infinite cycles
      if (seen.has(current)) {
        break;
      }
      seen.set(current, true);

      current._dirtyBits = current._dirtyBits | DirtyBit.Descendants;
      current = current.parent;
    }
  }

  forEachChild(fn: (c: FsNode, i: number) => void): void {
    let i = 0;
    for (let child = this.firstChild; child; child = child.next) {
      fn(child, i++);
    }
  }

  // Cause fuck browsers... that's why.
  // Fix for "dynamically creating SVG elements doesn't actually make xlink images work".
  // We check asynchronously if any children have this cursed attribute for loading SVG images. If so, we innerHTML ourselves once which fixes it.
  private _hasRenderFixScheduled = false;
  private async scheduleRenderFixer() {
    if (this._hasRenderFixScheduled) {
      return;
    }

    this._hasRenderFixScheduled = true;

    // Take a breath to finish building this.dom and adding it to the document, in case we
    // need to.
    await Promise.resolve();

    const oldElem = <HTMLElement>this.dom;
    if (!oldElem) {
      return;
    }

    const stage = document.createElement('div');
    // SECURITY NOTE: This is safe because the fsdom code that built oldElem removes
    // attributes and elements that can execute code.
    stage.innerHTML = oldElem.outerHTML;
    this.dom = stage.firstChild;
    if (oldElem.parentNode) {
      oldElem.parentNode.replaceChild(this.dom!, oldElem);
    }

    // Point mirror dom element references to the newly created subtree
    this.refreshChildDoms();
    this._hasRenderFixScheduled = false;
  }

  /**
   * Refresh children of svgs
   */
  private refreshChildDoms() {
    let mirror = this.firstChild;
    let live: Node | null = this.dom!.firstChild;
    while (mirror) {
      // Special case: Setting outerHTML can merge text nodes, changing the dom
      // structure. For simplicity, just delete the new text nodes and reinsert the old.
      //
      // Note that `live' can be null after a trailing text node.
      if (live && live.nodeType == Node.TEXT_NODE) {
        const tmp = live;
        live = tmp.nextSibling;
        this.dom!.removeChild(tmp);
        continue;
      }

      if (mirror.tag == TEXT_NODE_TAG) {
        this.dom!.insertBefore(mirror.dom!, live);
        mirror = mirror.next;
        continue;
      }

      if (
        Boolean(mirror) !== Boolean(live) ||
        toNormalizedTagName(live ? live.nodeName : '', 'svg') !== mirror.tag
      ) {
        console.error('Mirror dom out of sync');
        return;
      }

      // Normal case: attach mirror to new dom
      mirror.dom = live;
      mirror.refreshChildDoms();
      mirror = mirror.next;
      live = live ? live.nextSibling : null;
    }
  }

  private _tellParentSVGToFixItselfLater(): void {
    let cur = this.parent;
    const seen = new WeakMap<FsNode, boolean>();
    while (cur) {
      // protect against infinite cycles
      if (seen.has(cur)) {
        break;
      }
      seen.set(cur, true);

      if (cur.tag == 'svg') {
        cur.scheduleRenderFixer();
        return;
      }
      cur = cur.parent;
    }
  }

  /**
   * Flush the properties of this node to the real DOM and clear any relevant
   * dirty bits.
   *
   * Subclasses can override this method to add support for flushing properties
   * that are specific to a particular node type.
   *
   * @param dom - The DOM node that the properties should be flushed to.
   * Equivalent to 'this.dom' except that it's guaranteed to be non-null.
   *
   * TODO(sethf): Make this abstract once it's feasible.
   */
  protected flushProperties(dom: Node): void {
    const elem = dom as unknown as HTMLElement; // Only used for element-y things below.

    // NOTE (eugenechang) this has to be done prior to attributes for radio
    // because setting the checked property can alter the behavior for setting checked attribute
    // VAL-7658
    if (this._hasDirtyBit(DirtyBit.Property)) {
      for (const [name, value] of Object.entries(this._properties)) {
        if (name in elem) {
          (elem as unknown as Record<string, unknown>)[name] = value;
        } else {
          console.warn(`skipped setting unknown property "${name}" = ${value}`);
        }
      }
      this._clearDirtyBit(DirtyBit.Property);
    }

    if (this._hasDirtyAttrs()) {
      for (const attrName of this._dirtyAttrs) {
        if (Object.prototype.hasOwnProperty.call(this.attrs, attrName)) {
          this.flushAttr(dom, attrName, this.attrs[attrName]);
        } else {
          this.flushAttr(dom, attrName, null);
        }
      }
      this._clearDirtyAttrs();
    }

    // Flush helper inline styles after attributes, so that they'll be applied
    // even if the 'style' attribute changes.
    if (this._hasDirtyBit(DirtyBit.HelperInlineStyle)) {
      for (const [prop, { value, priority }] of this._helperStyles) {
        elem.style.setProperty(prop, value, priority);
      }
      this._clearDirtyBit(DirtyBit.HelperInlineStyle);
    }

    if (this._hasDirtyBit(DirtyBit.Value)) {
      setValue(elem as HTMLInputElement, this._value!);
      this._clearDirtyBit(DirtyBit.Value);
    }

    if (this._hasDirtyBit(DirtyBit.ScrollPosition)) {
      if (this._scrollState) {
        this.flushScrollPosition();
      }
      this._clearDirtyBit(DirtyBit.ScrollPosition);
    }
  }

  private _insert(child: FsNode, realBefore: FsNode | null) {
    let before = realBefore; // for eslint
    if (!child) {
      return;
    }

    this.setDirtyBit(DirtyBit.ChildList);

    if (before && before.parent != this) {
      console.log("Before isn't a child of this element");
      before = null;
    }

    // Ensure that this._footerChild always comes last.
    if (!before) {
      before = this._footerChild;
    }

    child.parent = this;

    // Mark the newly-inserted child as dirty. We do this for two reasons.
    // First, if the child was mutated before it was inserted, this ensures that
    // those mutations will get flushed. Second, it avoids a special case.
    // Virtually all nodes will get flushed regardless, since they'll have a
    // descendant or an attribute or some other thing to trigger the flush, but
    // without DirtyBit.NewlyAttached nodes with none of those things would
    // *never* get flushed. That doesn't necessarily cause a problem, since the
    // node would still get created when the parent flushes its child list, but
    // it's unintuitive, and might lead to bugs or confusing debugging sessions.
    child.setDirtyBit(DirtyBit.NewlyAttached);

    if (before == this.firstChild) {
      this.firstChild = child;
    }

    child.next = before;
    if (!before) {
      child.prev = this.lastChild;
      this.lastChild = child;
    } else {
      child.prev = before.prev;
    }

    if (child.prev) {
      child.prev.next = child;
    }
    if (child.next) {
      child.next.prev = child;
    }
  }

  getRendererBoundingRect(): DOMRect {
    return this.ownerDoc.getRendererBoundingRectFor(this);
  }

  getSelector(): SelectorChain | undefined {
    return this.ownerDoc.getSelectorFor(this);
  }

  getInnerTextFs(): string | undefined {
    return this.ownerDoc.getInnerTextFsFor(this);
  }
}
